import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { THERMAL_ACCEPT, THERMAL_MAX_BYTES, thermalFileProblem } from '../../utils/thermal/upload';

/*
 * Field Mode's Thermal step (docs/FIELD_AI_VISION_SPEC.md section 42): a
 * radiometric file the thermal camera's own app saved, imported byte for
 * byte (at most 25 MB) and sent by the outbox after the photos, linked to the
 * collection the batch becomes (utils/fieldOutbox.js). No browser reads
 * temperatures from a thermal camera on the phone: the vendor's app saves the
 * file, and this is where it comes in.
 *
 * Presentational: the batch editor keeps what is picked until Save (as it
 * does voice notes), the capture summary writes it to the batch at once.
 *
 *   value      [{id, name, size, sent, skipped, decoded, refused}]
 *   onAdd(file), onRemove(id)
 */
const mb = bytes => (bytes / (1024 * 1024)).toFixed(1);

export default function FieldThermalImport({ value = [], onAdd, onRemove, disabled = false, headingAs: H = 'h2',
  headingStyle, testid = 'field-thermal' }) {
  const { t } = useTranslation('thermal');
  const input = useRef(null);
  const [problem, setProblem] = useState('');
  const pick = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    setProblem('');
    for (const file of files) {
      const why = thermalFileProblem(file);
      if (why) {
        setProblem(t(`field.problem.${why}`, { name: file.name, max: mb(THERMAL_MAX_BYTES) }));
        continue;
      }
      onAdd?.(file);
    }
  };
  const stateOf = (item) => {
    if (item.sent) return t(item.decoded === false ? 'field.sentUndecoded' : 'field.sent');
    if (item.skipped) return t(item.lost ? 'field.lost' : 'field.refused');
    return t('field.waiting');
  };
  return (
    <section data-testid={testid} aria-labelledby={`${testid}-title`}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <H id={`${testid}-title`} style={headingStyle}>{t('field.title')}</H>
      <p style={hint}>{t('field.help')}</p>
      {value.length > 0 && (
        <ul style={list}>
          {value.map((item, i) => (
            <li key={item.id} style={row} data-testid={`${testid}-item-${i}`} data-state={item.sent ? 'sent' : item.skipped ? 'skipped' : 'waiting'}>
              <span aria-hidden="true">🌡️</span>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                <bdi>{item.name || t('field.unnamed')}</bdi>
                <span style={hint}> · <bdi dir="ltr">{mb(item.size || 0)} MB</bdi> · {stateOf(item)}</span>
              </span>
              {!item.sent && !disabled && onRemove && (
                <button type="button" style={removeBtn} data-testid={`${testid}-remove-${i}`}
                  aria-label={t('field.remove', { name: item.name || t('field.unnamed') })}
                  title={t('field.remove', { name: item.name || t('field.unnamed') })}
                  onClick={() => onRemove(item.id)}><span aria-hidden="true">×</span></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {problem && <p role="alert" style={warn} data-testid={`${testid}-problem`}>{problem}</p>}
      {onAdd && (
        <div>
          <button type="button" style={addBtn} disabled={disabled} data-testid={`${testid}-add-button`}
            title={t('field.addTip')} onClick={() => input.current?.click()}>
            {t('field.add')}
          </button>
          {/* The file as the camera's app saved it: never re-encoded, never resized. */}
          <input ref={input} type="file" accept={THERMAL_ACCEPT} multiple style={{ display: 'none' }} tabIndex={-1}
            aria-hidden="true" data-testid={`${testid}-input`} onChange={pick} />
        </div>
      )}
    </section>
  );
}

const hint = { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' };
const warn = { margin: 0, fontSize: 13, color: 'var(--c-danger-fg, var(--c-danger))' };
const list = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 };
const row = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 };
const removeBtn = { background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 16,
  cursor: 'pointer', padding: '2px 10px', color: 'var(--c-text-fg)', minHeight: 36 };
const addBtn = { background: 'var(--c-surface)', border: '1px solid var(--c-border-strong-2, var(--c-border-strong))',
  borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer', color: 'var(--c-text-fg)', minHeight: 44 };
